/**
 * Zento AI Widget API Client
 * 
 * Handles all communication with the Zento AI chat API.
 */

export interface RealtimeConfig {
  supabase_url: string;
  supabase_anon_key: string;
}

export interface AssistantInfo {
  name: string;
  welcome_message: string | null;
  primary_color: string;
  logo_url: string | null;
  bubble_icon_url: string | null;
  is_active: boolean;
  realtime_config?: RealtimeConfig;
}

// File attachment for multimodal support
export interface FileAttachment {
  type: 'image' | 'document' | 'file';  // 'file' for non-image/PDF files (Excel, Word, ZIP, etc.)
  media_type: string;
  data?: string; // base64 encoded (present when uploading, absent when loaded from history)
  filename?: string;
  public_url?: string; // For displaying images from storage (when loaded from history)
}

/**
 * Page context extracted from the current page.
 * Provides rich context about what the user is seeing and interacting with.
 */
export interface PageContext {
  /** Current URL of the page */
  url: string;
  /** Current pathname (cleaner than full URL) */
  pathname: string;
  /** Page title */
  title: string;
  /** Visible headings (H1-H4) that give structure context */
  headings: string[];
  /** All visible text on the page (truncated) */
  visibleText: string;
  /** Form inputs with their current values */
  inputs: PageInput[];
  /** Visible buttons (available actions) */
  buttons: string[];
  /** Visible links (available navigation) */
  links: string[];
}

export interface PageInput {
  /** Input type (text, email, checkbox, etc.) */
  type: string;
  /** Label or placeholder that identifies the field */
  label: string;
  /** Current value of the input */
  value: string;
  /** Whether the field has a validation error */
  isInvalid: boolean;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'human_agent' | 'system';
  content: string;
  created_at?: string;
  attachments?: FileAttachment[];
  sent_by_user_id?: string;
  sent_by_name?: string;
}

const WIDGET_HUMAN_AGENT_DISPLAY_NAME = 'Soporte';

export function sanitizeWidgetMessage<T extends ChatMessage>(message: T): T {
  if (message.role !== 'human_agent') {
    return message;
  }
  return { ...message, sent_by_name: WIDGET_HUMAN_AGENT_DISPLAY_NAME };
}

// Debug info structure (NEW: Cursor-like layered approach)
export interface DebugFileInfo {
  file_path: string;
  layer: string;
  file_type: string;
  similarity: number;
  relevance: string;
  is_full_file?: boolean;
}

export interface DebugLayerInfo {
  layer: string;
  files: Array<{
    file_path: string;
    type: string;
  }>;
}

export interface DebugInfo {
  query_analysis?: {
    business_concepts?: string[];
    search_keywords?: string[];
    query_intent?: string;
  } | null;
  files_by_layer: DebugLayerInfo[];
  files_details: DebugFileInfo[];
  context_tokens: number;
  retrieval_time_ms: number;
  llm_time_ms: number;
  iterations_used: number;
}

export interface SendMessageResponse {
  conversation_id: string;
  message_id: string;
  role: 'assistant';
  content: string;
  chunks_used: string[]; // File paths used for context
  response_time_ms: number;
  debug?: DebugInfo; // Optional debug info when include_debug=true
}

// Streaming event types
export type StreamEventType = 'thinking' | 'keepalive' | 'content_reset' | 'text_delta' | 'complete' | 'error';

export interface StreamEvent {
  type: StreamEventType;
  data: {
    message?: string;
    /** Safe-to-show UI copy when present; omit for technical/provider errors */
    client_facing_message?: string;
    content?: string;
    full_so_far?: string;
    conversation_id?: string;
    message_id?: string;
    response_time_ms?: number;
    human_mode?: boolean; // True if conversation is handled by human agent
  };
}

/** Normalized API error for widget UI (only clientFacingMessage is toast-safe). */
export interface WidgetApiError {
  clientFacingMessage?: string;
  message?: string;
  status?: number;
}

function extractClientFacingMessage(errorData: unknown): string | undefined {
  if (!errorData || typeof errorData !== 'object') return undefined;
  const data = errorData as Record<string, unknown>;
  if (typeof data.client_facing_message === 'string') {
    return data.client_facing_message;
  }
  const detail = data.detail;
  if (typeof detail === 'object' && detail !== null) {
    const nested = (detail as Record<string, unknown>).client_facing_message;
    if (typeof nested === 'string') return nested;
  }
  return undefined;
}

// Subscription event types for real-time updates
export type SubscriptionEventType = 'new_message' | 'assignment_changed' | 'ping' | 'error';

export interface SubscriptionEvent {
  type: SubscriptionEventType;
  data: {
    // For new_message
    id?: string;
    role?: 'user' | 'assistant' | 'human_agent' | 'system';
    content?: string;
    created_at?: string;
    sent_by_user_id?: string;
    sent_by_name?: string;
    // For assignment_changed
    assignment_type?: 'ai_agent' | 'human_agent';
    assigned_to?: string | null;
    // For ping
    timestamp?: number;
    // For error
    message?: string;
  };
}

export interface SubscriptionCallbacks {
  onNewMessage?: (message: ChatMessage) => void;
  onAssignmentChanged?: (assignmentType: string, assignedTo: string | null) => void;
  onPing?: () => void;
  onError?: (error: string) => void;
}

export interface StreamMessageCallbacks {
  onThinking?: () => void;
  onKeepalive?: (message: string) => void;  // Periodic status messages during long operations
  onContentReset?: () => void;
  onTextDelta?: (content: string, fullSoFar: string) => void;
  onComplete?: (response: SendMessageResponse) => void;
  onHumanMode?: (conversationId: string) => void; // Called when message sent to human-handled conversation
  onError?: (error: WidgetApiError) => void;
}

export interface ConversationSummary {
  id: string;
  session_id: string;
  message_count: number;
  started_at: string | null;
  last_message_at: string | null;
  first_message: string | null;
  /** Preview of the most recent message in the conversation (any role), like the inbox. */
  last_message: string | null;
  assignment_type?: 'ai_agent' | 'human_agent';
  assigned_to?: string | null;
  human_requested_at?: string | null;
  /** Number of unread messages from assistant/human_agent */
  unread_count?: number;
}

// Widget user information
export interface WidgetUserData {
  name?: string;
  email?: string;
}

/** Response from POST /api/chat/widget/identity */
export interface WidgetIdentityResponse {
  session_id: string;
  widget_user: {
    id: string;
    session_id: string;
    name: string | null;
    email: string | null;
    created_at: string;
    updated_at: string;
  };
}

// Request human response
export interface RequestHumanResponse {
  message: string;
  widget_user_id: string;
  conversation_id: string;
}

export interface ListConversationsResponse {
  conversations: ConversationSummary[];
  count: number;
  /** Total conversations matching the session (for pagination) */
  total?: number;
  has_more?: boolean;
  /** Total unread messages across all conversations */
  total_unread?: number;
}

export interface WidgetClientLinkResponse {
  linked: boolean;
  client_id?: string | null;
  client_name?: string | null;
  show_development_time?: boolean;
}

export interface WidgetTicketSummary {
  ticket_id: string;
  ticket_number?: number | null;
  ticket_label: string;
  title: string;
  status: string;
  development_time_minutes?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface WidgetTicketsListResponse {
  tickets: WidgetTicketSummary[];
  count: number;
  total?: number;
  has_more?: boolean;
}

export interface WidgetDevTimeMonthRow {
  month: string;
  label: string;
  total_minutes: number;
}

export interface WidgetDevTimeByMonthResponse {
  months: WidgetDevTimeMonthRow[];
}

/** Widget list + message pagination (aligned with inbox page sizes) */
export const WIDGET_CONVERSATIONS_PAGE_SIZE = 50;
export const WIDGET_TICKETS_PAGE_SIZE = 50;
export const WIDGET_MESSAGES_PAGE_SIZE = 50;

export interface WidgetApiClientConfig {
  apiKey: string;
  baseUrl: string;
}

export class WidgetApiClient {
  private apiKey: string;
  private baseUrl: string;

  constructor(config: WidgetApiClientConfig) {
    this.apiKey = config.apiKey;
    this.baseUrl = config.baseUrl.replace(/\/$/, ''); // Remove trailing slash
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-API-Key': this.apiKey,
      ...(options.headers as Record<string, string> || {}),
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.detail || `HTTP ${response.status}`);
      (error as any).status = response.status;
      (error as any).data = errorData;
      throw error;
    }

    return response.json();
  }

  /**
   * Get assistant information (name, welcome message, colors)
   */
  async getAssistantInfo(): Promise<AssistantInfo> {
    return this.request<AssistantInfo>('/api/chat/assistant');
  }

  /**
   * Send a message and stream the AI response using Server-Sent Events.
   * This avoids timeout issues by keeping the connection open.
   */
  async sendMessageStream(
    sessionId: string,
    message: string,
    callbacks: StreamMessageCallbacks,
    conversationId?: string,
    forceNewConversation?: boolean,
    attachments?: FileAttachment[],
    pageContext?: PageContext,
  ): Promise<void> {
    const url = `${this.baseUrl}/api/chat/message/stream`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': this.apiKey,
      },
      body: JSON.stringify({
        session_id: sessionId,
        message,
        conversation_id: conversationId,
        force_new_conversation: forceNewConversation || false,
        attachments: attachments && attachments.length > 0 ? attachments : undefined,
        page_context: pageContext,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const detail = (errorData as { detail?: unknown }).detail;
      const technical =
        typeof detail === 'string'
          ? detail
          : detail && typeof detail === 'object'
            ? JSON.stringify(detail)
            : `HTTP ${response.status}`;
      callbacks.onError?.({
        clientFacingMessage: extractClientFacingMessage(errorData),
        message: technical,
        status: response.status,
      });
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      callbacks.onError?.({ message: 'No response body' });
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const eventData = JSON.parse(line.slice(6)) as StreamEvent;

              switch (eventData.type) {
                case 'thinking':
                  callbacks.onThinking?.();
                  break;

                case 'keepalive':
                  // Periodic status messages to keep connection alive
                  if (eventData.data.message) {
                    callbacks.onKeepalive?.(eventData.data.message);
                  }
                  break;

                case 'content_reset':
                  callbacks.onContentReset?.();
                  break;

                case 'text_delta':
                  if (eventData.data.content && eventData.data.full_so_far) {
                    callbacks.onTextDelta?.(
                      eventData.data.content,
                      eventData.data.full_so_far
                    );
                  }
                  break;

                case 'complete':
                  // Check if this is a human mode response (no AI, message goes to human agent)
                  if (eventData.data.human_mode) {
                    callbacks.onHumanMode?.(eventData.data.conversation_id || '');
                  } else if (eventData.data.content !== undefined) {
                    callbacks.onComplete?.({
                      conversation_id: eventData.data.conversation_id || '',
                      message_id: eventData.data.message_id || '',
                      role: 'assistant',
                      content: eventData.data.content,
                      chunks_used: [],
                      response_time_ms: eventData.data.response_time_ms || 0,
                    });
                  }
                  break;

                case 'error':
                  callbacks.onError?.({
                    clientFacingMessage: eventData.data.client_facing_message,
                    message: eventData.data.message || 'Unknown error',
                  });
                  break;
              }
            } catch {
              // Skip malformed JSON
              console.warn('[ZentoWidget] Malformed SSE event:', line);
            }
          }
        }
      }
    } catch (error) {
      callbacks.onError?.({
        message: error instanceof Error ? error.message : 'Stream error',
      });
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Get message history for a session
   */
  async getMessageHistory(
    sessionId: string,
    limit: number = WIDGET_MESSAGES_PAGE_SIZE
  ): Promise<{ messages: ChatMessage[]; count: number }> {
    const response = await this.request<{ messages: ChatMessage[]; count: number }>(
      `/api/chat/conversation/${encodeURIComponent(sessionId)}/messages?limit=${limit}`
    );
    return {
      ...response,
      messages: response.messages.map(sanitizeWidgetMessage),
    };
  }

  /**
   * List all conversations for a session
   */
  async listConversations(
    sessionId: string,
    options: { limit?: number; offset?: number } = {}
  ): Promise<ListConversationsResponse> {
    const limit = options.limit ?? WIDGET_CONVERSATIONS_PAGE_SIZE;
    const offset = options.offset ?? 0;
    return this.request<ListConversationsResponse>(
      `/api/chat/conversations?session_id=${encodeURIComponent(sessionId)}&limit=${limit}&offset=${offset}`
    );
  }

  /**
   * Mark a conversation as read for the current session.
   * This updates the last_read_at timestamp so subsequent
   * unread counts will only include newer messages.
   */
  async markConversationAsRead(
    conversationId: string,
    sessionId: string
  ): Promise<{ status: string; last_read_at: string }> {
    return this.request<{ status: string; last_read_at: string }>(
      `/api/chat/conversations/${encodeURIComponent(conversationId)}/mark-read?session_id=${encodeURIComponent(sessionId)}`,
      { method: 'POST' }
    );
  }

  /**
   * Get messages for a specific conversation by ID
   */
  async getConversationMessages(
    conversationId: string,
    options: {
      limit?: number;
      offset?: number;
      /** Initial load: most recent page (same as inbox) */
      fromEnd?: boolean;
    } = {}
  ): Promise<{
    conversation_id: string;
    messages: ChatMessage[];
    count: number;
    total?: number;
    has_more?: boolean;
    offset?: number;
    assignment_type?: 'ai_agent' | 'human_agent';
    assigned_to?: string | null;
    human_requested_at?: string | null;
  }> {
    const limit = options.limit ?? WIDGET_MESSAGES_PAGE_SIZE;
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (options.fromEnd) {
      params.set('from_end', 'true');
    } else if (options.offset !== undefined) {
      params.set('offset', String(options.offset));
    }
    const response = await this.request<{
      conversation_id: string;
      messages: ChatMessage[];
      count: number;
      total?: number;
      has_more?: boolean;
      offset?: number;
      assignment_type?: 'ai_agent' | 'human_agent';
      assigned_to?: string | null;
      human_requested_at?: string | null;
    }>(
      `/api/chat/conversation-by-id/${encodeURIComponent(conversationId)}/messages?${params.toString()}`
    );
    return {
      ...response,
      messages: response.messages.map(sanitizeWidgetMessage),
    };
  }

  /**
   * Request to speak with a human agent.
   * Requires name and email for follow-up.
   */
  async requestHuman(
    sessionId: string,
    conversationId: string,
    name: string,
    email: string
  ): Promise<RequestHumanResponse> {
    return this.request<RequestHumanResponse>('/api/chat/request-human', {
      method: 'POST',
      body: JSON.stringify({
        session_id: sessionId,
        conversation_id: conversationId,
        name,
        email,
      }),
    });
  }

  /**
   * Get widget user data by session ID.
   */
  async getWidgetUser(sessionId: string): Promise<WidgetUserData | null> {
    try {
      return await this.request<WidgetUserData>(
        `/api/chat/widget-user/${encodeURIComponent(sessionId)}`
      );
    } catch (error: any) {
      if (error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Update widget user data.
   */
  async updateWidgetUser(
    sessionId: string,
    data: Partial<WidgetUserData>
  ): Promise<WidgetUserData> {
    return this.request<WidgetUserData>(
      `/api/chat/widget-user/${encodeURIComponent(sessionId)}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Exchange a host-signed JWT for a stable session id and widget user (SSO).
   */
  async exchangeWidgetIdentity(token: string): Promise<WidgetIdentityResponse> {
    return this.request<WidgetIdentityResponse>('/api/chat/widget/identity', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  /**
   * Check if the widget session email is linked to a CRM client.
   */
  async getWidgetClientLink(sessionId: string): Promise<WidgetClientLinkResponse> {
    return this.request<WidgetClientLinkResponse>(
      `/api/chat/widget-client-link?session_id=${encodeURIComponent(sessionId)}`,
    );
  }

  /**
   * List tickets for a widget user linked to a CRM client.
   */
  async getWidgetTickets(
    sessionId: string,
    options: { limit?: number; offset?: number } = {},
  ): Promise<WidgetTicketsListResponse> {
    const limit = options.limit ?? WIDGET_TICKETS_PAGE_SIZE;
    const offset = options.offset ?? 0;
    return this.request<WidgetTicketsListResponse>(
      `/api/chat/widget-tickets?session_id=${encodeURIComponent(sessionId)}&limit=${limit}&offset=${offset}`,
    );
  }

  async getWidgetDevelopmentTimeByMonth(sessionId: string): Promise<WidgetDevTimeByMonthResponse> {
    return this.request<WidgetDevTimeByMonthResponse>(
      `/api/chat/widget-development-time-by-month?session_id=${encodeURIComponent(sessionId)}`,
    );
  }

  /**
   * Download ticket PDF (client view) as a Blob for in-widget preview.
   */
  async fetchWidgetTicketPdf(sessionId: string, ticketId: string): Promise<Blob> {
    const url = `${this.baseUrl}/api/chat/widget-tickets/${encodeURIComponent(ticketId)}/pdf?session_id=${encodeURIComponent(sessionId)}`;
    const response = await fetch(url, {
      headers: {
        'X-API-Key': this.apiKey,
      },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(
        (errorData as { detail?: string }).detail || `HTTP ${response.status}`,
      );
      (error as Error & { status?: number }).status = response.status;
      throw error;
    }
    return response.blob();
  }

  /**
   * Subscribe to real-time message updates for a conversation.
   * 
   * This is used when a conversation is being handled by a human agent
   * to receive their replies in real-time.
   * 
   * Returns an abort controller to stop the subscription.
   * @deprecated Use Supabase Realtime broadcast channels instead.
   */
  subscribeToConversation(
    conversationId: string,
    callbacks: SubscriptionCallbacks,
    lastMessageId?: string,
  ): AbortController {
    const abortController = new AbortController();
    const baseUrl = new URL(`${this.baseUrl}/api/chat/subscribe/${encodeURIComponent(conversationId)}`);
    if (lastMessageId) {
      baseUrl.searchParams.set('last_message_id', lastMessageId);
    }

    const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    const connect = async () => {
      let retryDelay = 2000;
      while (!abortController.signal.aborted) {
        try {
          const response = await fetch(baseUrl.toString(), {
            method: 'GET',
            headers: {
              'X-API-Key': this.apiKey,
            },
            signal: abortController.signal,
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            callbacks.onError?.(errorData.detail || `HTTP ${response.status}`);
            await sleep(retryDelay);
            retryDelay = Math.min(retryDelay * 2, 15000);
            continue;
          }

          retryDelay = 2000;
          const reader = response.body?.getReader();
          if (!reader) {
            callbacks.onError?.('No response body');
            await sleep(retryDelay);
            continue;
          }

          const decoder = new TextDecoder();
          let buffer = '';

          while (!abortController.signal.aborted) {
            const { done, value } = await reader.read();

            if (done) {
              console.log('[ZentoWidget] Subscription ended, reconnecting...');
              break;
            }

            buffer += decoder.decode(value, { stream: true });

            // Parse SSE events from buffer
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const eventData = JSON.parse(line.slice(6)) as SubscriptionEvent;

                  switch (eventData.type) {
                    case 'new_message':
                      if (eventData.data.id && eventData.data.role && eventData.data.content !== undefined) {
                        callbacks.onNewMessage?.(sanitizeWidgetMessage({
                          id: eventData.data.id,
                          role: eventData.data.role,
                          content: eventData.data.content,
                          created_at: eventData.data.created_at,
                          sent_by_user_id: eventData.data.sent_by_user_id,
                          sent_by_name: eventData.data.sent_by_name,
                        }));
                      }
                      break;

                    case 'assignment_changed':
                      if (eventData.data.assignment_type) {
                        callbacks.onAssignmentChanged?.(
                          eventData.data.assignment_type,
                          eventData.data.assigned_to || null
                        );
                      }
                      break;

                    case 'ping':
                      callbacks.onPing?.();
                      break;

                    case 'error':
                      callbacks.onError?.(eventData.data.message || 'Unknown error');
                      break;
                  }
                } catch {
                  console.warn('[ZentoWidget] Malformed subscription event:', line);
                }
              }
            }
          }
        } catch (error) {
          if ((error as Error).name !== 'AbortError') {
            callbacks.onError?.(error instanceof Error ? error.message : 'Subscription error');
          }
        }

        if (!abortController.signal.aborted) {
          await sleep(retryDelay);
          retryDelay = Math.min(retryDelay * 2, 15000);
        }
      }
    };

    // Start the subscription
    connect();

    return abortController;
  }
}

/**
 * Generate a unique session ID for the current user/browser (per embed API key).
 */
export function generateSessionId(apiKey: string): string {
  const storageKey = `zento_session_id_${apiKey.substring(0, 8)}`;
  let sessionId = localStorage.getItem(storageKey);

  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    try {
      localStorage.setItem(storageKey, sessionId);
    } catch {
      console.warn('[ZentoWidget] localStorage not available, using session-only ID');
    }
  }

  return sessionId;
}

/** Persist session id after SSO exchange so reloads stay on the same logical session. */
export function persistSessionIdForApiKey(apiKey: string, sessionId: string): void {
  const storageKey = `zento_session_id_${apiKey.substring(0, 8)}`;
  try {
    localStorage.setItem(storageKey, sessionId);
  } catch {
    /* ignore */
  }
}

/**
 * Store conversation ID for session continuity
 */
export function storeConversationId(apiKey: string, conversationId: string): void {
  const key = `zento_conversation_${apiKey.substring(0, 8)}`;
  try {
    localStorage.setItem(key, conversationId);
  } catch {
    // Ignore storage errors
  }
}
